/**
 * Safe return paths and auth entry helpers.
 * Prefer Create Account for gated actions; keep Sign In one click away.
 */

const AUTH_PATH_PREFIXES = ['/login', '/register', '/reset-password'];

export function isSafeInternalPath(path) {
  if (!path || typeof path !== 'string') return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  return !AUTH_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}/`));
}

export function locationToPath(locationLike) {
  if (!locationLike) return null;
  if (typeof locationLike === 'string') return locationLike;
  if (typeof locationLike === 'object' && locationLike.pathname) {
    return `${locationLike.pathname}${locationLike.search || ''}${locationLike.hash || ''}`;
  }
  return null;
}

export function getSafeReturnPath(from, fallback = '/') {
  const path = locationToPath(from);
  if (path && isSafeInternalPath(path)) return path;
  return fallback;
}

export function resolveReturnPath({ search, state } = {}, fallback = '/') {
  const params = new URLSearchParams(typeof search === 'string' ? search : '');
  const next = params.get('next');
  if (next && isSafeInternalPath(next)) return next;
  return getSafeReturnPath(state?.from, fallback);
}

export function buildRegisterPath(returnTo, { requireAuth = false } = {}) {
  const params = new URLSearchParams();
  if (returnTo && isSafeInternalPath(returnTo)) params.set('next', returnTo);
  if (requireAuth) params.set('continue', '1');
  const query = params.toString();
  return query ? `/register?${query}` : '/register';
}

export function buildLoginPath(returnTo) {
  const params = new URLSearchParams();
  if (returnTo && isSafeInternalPath(returnTo)) params.set('next', returnTo);
  const query = params.toString();
  return query ? `/login?${query}` : '/login';
}

/**
 * First-time-friendly gate: Create Account first, Sign In available on that page.
 */
export function navigateRequireAuth(navigate, location, options = {}) {
  const returnTo =
    options.returnTo
    || locationToPath(location)
    || '/';
  navigate(buildRegisterPath(returnTo, { requireAuth: true }), {
    state: {
      from: returnTo,
      requireAuth: true,
      ...(options.state || {}),
    },
  });
}
